import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  getGetCollectionTicketClaimsQueryKey,
  getListCollectionTicketClaimMemberStatesQueryKey,
  getListTenantCollectionTicketClaimsQueryKey,
  useCreateCollectionTicketClaim,
  useDeleteCollectionTicketClaim,
  useGetCollectionTicketClaims,
  useGetCurrentOrganization,
  useUpdateCollectionTicketClaim,
} from '@/api/trafikportalen';
import { getTenantConfiguration } from '@/constants/tenantConfiguration';
import {
  toPeriodEndTimestamp,
  toPeriodStartTimestamp,
} from '@/helpers/periodEndDate';
import { hasActiveOrScheduledClaim } from '@/hooks/useCollectionClaimWarning';
import { useTenantID } from '@/hooks/useTenantID';
import { toast } from '@/hooks/useToast';
import { IconInfo, IconPlus } from '@/icons';
import { Spinner } from '../core/Spinner';
import Button from '../core/Button';
import type { OptionsType } from '../core/Dropdown';
import { NoClaimWarning } from '../NoClaimWarning';
import { CreatePeriodDialog } from './CreatePeriodDialog';
import { EditPeriodDialog } from './EditPeriodDialog';
import { DeletePeriodDialog } from './DeletePeriodDialog';
import { PeriodListItem } from './PeriodListItem';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';
import type { CreatePeriodFormData } from './CreatePeriodDialog';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';
import { useAuth } from '@/stores/auth';
import {
  collectionTypeToStudentType,
  isStudentCollectionType,
  STUDENT_TYPE_VIEW_ROUTE,
  StudentCollectionType,
} from '@/helpers/collectionTypes';
import { findOverlappingClaimIds } from '@/helpers/periodOverlap';
import { WarningBanner } from '../core/WarningBanner';

interface StudentCategoryPeriodsProps {
  collectionId: string;
  collectionType: CollectionTypeDTO;
}

export const StudentCategoryPeriods = ({
  collectionId,
  collectionType,
}: StudentCategoryPeriodsProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const tenantId = useTenantID();
  const accessibleCategories = useAccessibleStudentCategories({
    requireWriteAccess: true,
  });
  const { userProfile } = useAuth();
  const isOrgUser = !!userProfile?.organization_id;
  const { data: currentOrganization, isSuccess: organizationLoaded } =
    useGetCurrentOrganization({
      query: { enabled: isOrgUser },
    });
  const orgManagesPeriods =
    currentOrganization?.selfmanaged_school_claims === true;
  const periodEditingDisabled =
    isOrgUser && organizationLoaded && !orgManagesPeriods;
  const canManagePeriods =
    orgManagesPeriods &&
    accessibleCategories.includes(collectionType as StudentCollectionType);
  const studentType = isStudentCollectionType(collectionType)
    ? collectionTypeToStudentType(collectionType)
    : undefined;

  const { data: ticketClaims, isLoading: claimsLoading } =
    useGetCollectionTicketClaims(collectionId, {
      query: { enabled: !!collectionId },
    });

  const createClaim = useCreateCollectionTicketClaim();
  const updateClaim = useUpdateCollectionTicketClaim();
  const deleteClaim = useDeleteCollectionTicketClaim();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingClaim, setEditingClaim] =
    useState<CollectionTicketClaimDTO | null>(null);
  const [deletingClaim, setDeletingClaim] =
    useState<CollectionTicketClaimDTO | null>(null);

  const { claimType } = getTenantConfiguration(tenantId);

  const revokeStrategyOptions: OptionsType[] = useMemo(
    () => [
      {
        id: 'none',
        label: formatMessage({ defaultMessage: 'Ingen' }),
        value: 'none',
      },
      {
        id: 'revoke',
        label: formatMessage({ defaultMessage: 'Makulera' }),
        value: 'revoke',
      },
    ],
    [formatMessage],
  );

  const sortedClaims = useMemo(() => {
    if (!ticketClaims) return [];
    return [...ticketClaims].sort((a, b) => {
      const aFrom = a.valid_from ?? '';
      const bFrom = b.valid_from ?? '';
      return bFrom.localeCompare(aFrom);
    });
  }, [ticketClaims]);

  const overlappingClaimIds = useMemo(
    () => findOverlappingClaimIds(ticketClaims),
    [ticketClaims],
  );

  const invalidatePeriodQueries = useCallback(
    async (claimIds: string[] = []) => {
      if (!collectionId) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetCollectionTicketClaimsQueryKey(collectionId),
        }),
        queryClient.invalidateQueries({
          queryKey: getListTenantCollectionTicketClaimsQueryKey(),
        }),
        ...claimIds.map((claimId) =>
          queryClient.invalidateQueries({
            queryKey: getListCollectionTicketClaimMemberStatesQueryKey(claimId),
          }),
        ),
      ]);
    },
    [queryClient, collectionId],
  );

  const handleCreate = useCallback(
    async (data: CreatePeriodFormData) => {
      if (!canManagePeriods) return;

      try {
        await createClaim.mutateAsync({
          collectionID: collectionId,
          data,
        });

        toast.success(
          { title: formatMessage({ defaultMessage: 'Perioden skapades' }) },
          { autoClose: 3000 },
        );

        await invalidatePeriodQueries();
        setShowCreateDialog(false);
      } catch {
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Kunde inte skapa perioden',
            }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      canManagePeriods,
      collectionId,
      createClaim,
      formatMessage,
      invalidatePeriodQueries,
    ],
  );

  const handleUpdate = useCallback(
    async (validFrom: string, validTo: string) => {
      if (!canManagePeriods || !editingClaim) return;

      try {
        const resolvedFrom =
          validFrom.length > 10 ? validFrom : toPeriodStartTimestamp(validFrom);
        const resolvedTo =
          validTo.length > 10 ? validTo : toPeriodEndTimestamp(validTo, false);

        await updateClaim.mutateAsync({
          collectionTicketClaimID: editingClaim.id,
          data: {
            valid_from: resolvedFrom,
            valid_to: resolvedTo,
          },
        });

        const endedImmediately = validTo.length > 10;
        const optimisticTo = endedImmediately
          ? DateTime.now()
              .minus({ milliseconds: 10 })
              .toISO({ suppressMilliseconds: true })!
          : resolvedTo;

        if (endedImmediately) {
          queryClient.setQueryData<CollectionTicketClaimDTO[]>(
            getGetCollectionTicketClaimsQueryKey(collectionId),
            (old) =>
              old?.map((c) =>
                c.id === editingClaim.id
                  ? { ...c, valid_from: resolvedFrom, valid_to: optimisticTo }
                  : c,
              ),
          );
          // A refetch would restore the server end time, which is still a few
          // seconds in the future, so the period would look active again.
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetCollectionTicketClaimsQueryKey(collectionId),
              refetchType: 'none',
            }),
            queryClient.invalidateQueries({
              queryKey: getListTenantCollectionTicketClaimsQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: getListCollectionTicketClaimMemberStatesQueryKey(
                editingClaim.id,
              ),
            }),
          ]);
        } else {
          await invalidatePeriodQueries([editingClaim.id]);
        }

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Perioden uppdaterades',
            }),
          },
          { autoClose: 3000 },
        );
        setEditingClaim(null);
      } catch {
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Kunde inte uppdatera perioden',
            }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      canManagePeriods,
      editingClaim,
      updateClaim,
      formatMessage,
      invalidatePeriodQueries,
      queryClient,
      collectionId,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!canManagePeriods || !deletingClaim) return;

    try {
      await deleteClaim.mutateAsync({
        collectionTicketClaimID: deletingClaim.id,
      });

      queryClient.setQueryData<CollectionTicketClaimDTO[]>(
        getGetCollectionTicketClaimsQueryKey(collectionId),
        (old) => old?.filter((claim) => claim.id !== deletingClaim.id),
      );

      toast.success(
        { title: formatMessage({ defaultMessage: 'Perioden raderades' }) },
        { autoClose: 3000 },
      );

      await invalidatePeriodQueries([deletingClaim.id]);
      setDeletingClaim(null);
    } catch {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte radera perioden',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    canManagePeriods,
    deletingClaim,
    deleteClaim,
    formatMessage,
    invalidatePeriodQueries,
    queryClient,
    collectionId,
  ]);

  if (claimsLoading) {
    return (
      <div className="flex h-24 w-full items-center justify-center">
        <Spinner className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3>
            <FormattedMessage defaultMessage="Perioder" />
          </h3>
          <div className="flex items-center gap-2">
            {studentType && (
              <Button
                variant="ghost"
                startIcon={<IconInfo />}
                to={STUDENT_TYPE_VIEW_ROUTE[studentType]}
                params={{ tenantId, view: 'info' }}
              >
                <FormattedMessage defaultMessage="Läs mer" />
              </Button>
            )}
            {canManagePeriods && (
              <Button
                variant="ghost"
                startIcon={<IconPlus />}
                onClick={() => setShowCreateDialog(true)}
              >
                <FormattedMessage defaultMessage="Ny period" />
              </Button>
            )}
          </div>
        </div>
        <p className="text-text-700">
          {periodEditingDisabled ? (
            <FormattedMessage defaultMessage="Perioder för biljetter i denna kategori. Detta styrs av systemadministratörerna." />
          ) : (
            <FormattedMessage defaultMessage="Hantera perioder för biljetter i denna kategori. Varje period anger ett tidsspann under vilken eleverna ska få biljett." />
          )}
        </p>

        {!hasActiveOrScheduledClaim(ticketClaims) && (
          <NoClaimWarning canCreate={!periodEditingDisabled} />
        )}

        {overlappingClaimIds.size > 0 && orgManagesPeriods && (
          <WarningBanner>
            <FormattedMessage defaultMessage="Det finns perioder som överlappar varandra. Elever i kategorin kan få biljetter från flera perioder samtidigt." />
          </WarningBanner>
        )}

        {sortedClaims.length === 0 ? (
          <p className="py-4 text-center italic text-text-500">
            <FormattedMessage defaultMessage="Inga perioder har skapats ännu." />
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {sortedClaims.map((claim) => (
              <PeriodListItem
                key={claim.id}
                claim={claim}
                collectionType={collectionType}
                revokeStrategyOptions={revokeStrategyOptions}
                onEdit={setEditingClaim}
                onDelete={setDeletingClaim}
                overlapping={
                  orgManagesPeriods && overlappingClaimIds.has(claim.id)
                }
                readOnly={!canManagePeriods}
              />
            ))}
          </ul>
        )}
      </div>

      <CreatePeriodDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreate}
        isSubmitting={createClaim.isPending}
        claimType={claimType}
        collectionType={collectionType}
        existingClaims={ticketClaims}
      />

      <EditPeriodDialog
        claim={editingClaim}
        onClose={() => setEditingClaim(null)}
        onSave={handleUpdate}
        isSaving={updateClaim.isPending}
        existingClaims={ticketClaims}
      />

      <DeletePeriodDialog
        claim={deletingClaim}
        onClose={() => setDeletingClaim(null)}
        onConfirm={handleDelete}
        loading={deleteClaim.isPending}
      />
    </>
  );
};
